import { displayUserName } from "@modules/user/deletedUser";
import React from "react";
import { format } from "date-fns";
import { CheckCircle2, Upload } from "lucide-react";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { Button } from "~/components/ui/button";
import { CopyButton } from "~/components/ui/copy-button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useFirmwareInstallLogsQuery,
  useFirmwareVersionsQuery,
  useSetActiveFirmwareVersionMutation,
  useUploadFirmwareMutation,
} from "generated/hooks";
import { Routes } from "~/routes";

const formatTimestamp = (value?: string | null) =>
  value ? format(new Date(value), "yyyy-MM-dd HH:mm:ss") : "-";

const formatBytes = (value: number) =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  }).format(value / 1024) + " KB";

const userName = (
  user?: { fullName?: string | null; email?: string | null } | null
) => displayUserName(user);

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(reader.error ?? new Error("Failed to read file."));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const [, base64] = result.split(",", 2);
      resolve(base64 ?? "");
    };
    reader.readAsDataURL(file);
  });

const getLogIdentifier = (log: {
  eruName?: string | null;
  runtimeSerialNumber?: string | null;
  runtimePortName?: string | null;
  dfuSerialNumber?: string | null;
  eru?: {
    name?: string | null;
    bleId?: string | null;
    bleMac?: string | null;
  } | null;
}) =>
  log.eru?.name?.trim() ||
  log.eruName?.trim() ||
  log.eru?.bleId?.trim() ||
  log.eru?.bleMac?.trim() ||
  log.runtimeSerialNumber?.trim() ||
  log.dfuSerialNumber?.trim() ||
  log.runtimePortName?.trim() ||
  "Unknown ERU";

const FirmwarePage = () => {
  const [version, setVersion] = React.useState("");
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [activate, setActivate] = React.useState(true);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [versionsAfter, setVersionsAfter] = React.useState<string | null>(null);
  const [logsAfter, setLogsAfter] = React.useState<string | null>(null);
  const [versionCursorHistory, setVersionCursorHistory] = React.useState<
    (string | null)[]
  >([]);
  const [logCursorHistory, setLogCursorHistory] = React.useState<
    (string | null)[]
  >([]);

  const {
    data: versionsData,
    loading: versionsLoading,
    error: versionsError,
    refetch: refetchVersions,
  } = useFirmwareVersionsQuery({
    fetchPolicy: "network-only",
    variables: { after: versionsAfter },
  });
  const {
    data: logsData,
    loading: logsLoading,
    error: logsError,
    refetch: refetchLogs,
  } = useFirmwareInstallLogsQuery({
    fetchPolicy: "network-only",
    variables: { after: logsAfter },
  });
  const [uploadFirmware, { loading: uploading }] = useUploadFirmwareMutation();
  const [setActiveFirmwareVersion, { loading: activating }] =
    useSetActiveFirmwareVersionMutation();

  const versions = versionsData?.firmwareVersions?.nodes?.filter(Boolean) ?? [];
  const logs = logsData?.firmwareInstallLogs?.nodes?.filter(Boolean) ?? [];
  const versionsPageInfo = versionsData?.firmwareVersions?.pageInfo;
  const logsPageInfo = logsData?.firmwareInstallLogs?.pageInfo;

  const handleUpload = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const normalizedVersion = version.trim();

    if (!normalizedVersion) {
      setFormError("Enter a version number.");
      return;
    }

    if (!selectedFile) {
      setFormError("Choose a .bin firmware file.");
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith(".bin")) {
      setFormError("Firmware file must be a .bin file.");
      return;
    }

    try {
      const base64Data = await fileToBase64(selectedFile);
      await uploadFirmware({
        variables: {
          input: {
            version: normalizedVersion,
            fileName: selectedFile.name,
            base64Data,
            activate,
          },
        },
      });
      setVersion("");
      setSelectedFile(null);
      setVersionsAfter(null);
      setLogsAfter(null);
      setVersionCursorHistory([]);
      setLogCursorHistory([]);
      await Promise.all([
        refetchVersions({ after: null }),
        refetchLogs({ after: null }),
      ]);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Upload failed.");
    }
  };

  const handleActivate = async (id: string) => {
    setFormError(null);
    try {
      await setActiveFirmwareVersion({ variables: { id } });
      await refetchVersions();
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "Failed to activate firmware version."
      );
    }
  };

  const goToNextVersionPage = () => {
    setVersionCursorHistory((history) => [...history, versionsAfter]);
    setVersionsAfter(versionsPageInfo?.endCursor ?? null);
  };

  const goToPreviousVersionPage = () => {
    setVersionCursorHistory((history) => {
      const previousCursor = history.at(-1) ?? null;
      setVersionsAfter(previousCursor);
      return history.slice(0, -1);
    });
  };

  const goToNextLogPage = () => {
    setLogCursorHistory((history) => [...history, logsAfter]);
    setLogsAfter(logsPageInfo?.endCursor ?? null);
  };

  const goToPreviousLogPage = () => {
    setLogCursorHistory((history) => {
      const previousCursor = history.at(-1) ?? null;
      setLogsAfter(previousCursor);
      return history.slice(0, -1);
    });
  };

  return (
    <PageTemplate
      title="Firmware"
      breadcrumbs={[
        { label: "Admin", path: Routes.AdminDashboard },
        { label: "Firmware", path: Routes.AdminFirmware },
      ]}
    >
      <Tabs defaultValue="files" className="space-y-6">
        <TabsList>
          <TabsTrigger value="files">Firmware files</TabsTrigger>
          <TabsTrigger value="logs">Install logs</TabsTrigger>
        </TabsList>

        <TabsContent value="files" className="space-y-6">
          <form
            className="grid gap-4 rounded-md border bg-background p-4 md:grid-cols-[1fr_1fr_auto_auto]"
            onSubmit={handleUpload}
          >
            <div className="space-y-2">
              <Label htmlFor="firmware-version">Version number</Label>
              <Input
                id="firmware-version"
                value={version}
                onChange={(event) => setVersion(event.target.value)}
                placeholder="1.2.3"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="firmware-file">Firmware .bin</Label>
              <Input
                id="firmware-file"
                type="file"
                accept=".bin"
                onChange={(event) =>
                  setSelectedFile(event.target.files?.[0] ?? null)
                }
              />
            </div>
            <div className="flex items-end gap-2 pb-2">
              <Switch
                checked={activate}
                onCheckedChange={setActivate}
                id="activate-upload"
              />
              <Label htmlFor="activate-upload">Make active after upload</Label>
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={uploading}>
                <Upload className="mr-2 size-4" />
                {uploading ? "Uploading" : "Upload"}
              </Button>
            </div>
            {formError && (
              <p className="text-sm text-destructive md:col-span-4">
                {formError}
              </p>
            )}
          </form>

          {versionsError ? (
            <p className="text-sm text-destructive">
              Firmware versions could not be loaded.
            </p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Version</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead>Uploaded by</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>SHA-256</TableHead>
                    <TableHead>Installs</TableHead>
                    <TableHead>Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versionsLoading ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        Loading firmware versions...
                      </TableCell>
                    </TableRow>
                  ) : versions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        No firmware versions uploaded.
                      </TableCell>
                    </TableRow>
                  ) : (
                    versions.map((firmware) => (
                      <TableRow key={firmware.id}>
                        <TableCell className="font-medium">
                          {firmware.version}
                        </TableCell>
                        <TableCell>{firmware.fileName}</TableCell>
                        <TableCell>
                          {formatTimestamp(firmware.createdAt)}
                        </TableCell>
                        <TableCell>
                          {userName(firmware.uploadedByUser)}
                        </TableCell>
                        <TableCell>
                          {formatBytes(firmware.byteLength)}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          <div className="flex items-center gap-1">
                            <span className="max-w-[180px] truncate">
                              {firmware.sha256}
                            </span>
                            <CopyButton
                              value={firmware.sha256}
                              label="SHA-256 checksum"
                            />
                          </div>
                        </TableCell>
                        <TableCell>{firmware.installCount}</TableCell>
                        <TableCell>
                          {firmware.isActive ? (
                            <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700">
                              <CheckCircle2 className="size-4" />
                              Active
                            </span>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={activating}
                              onClick={() => handleActivate(firmware.id)}
                            >
                              Make active
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {(versionCursorHistory.length > 0 ||
                versionsPageInfo?.hasNextPage) && (
                <div className="flex justify-end gap-2 border-t p-3">
                  {versionCursorHistory.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={versionsLoading}
                      onClick={goToPreviousVersionPage}
                    >
                      Previous firmware versions
                    </Button>
                  )}
                  {versionsPageInfo?.hasNextPage && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={versionsLoading}
                      onClick={goToNextVersionPage}
                    >
                      Next firmware versions
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="logs">
          {logsError ? (
            <p className="text-sm text-destructive">
              Firmware install logs could not be loaded.
            </p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requested</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>ERU</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Firmware</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Summary</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logsLoading ? (
                    <TableRow>
                      <TableCell colSpan={8}>Loading install logs...</TableCell>
                    </TableRow>
                  ) : logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        No firmware installs logged.
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{formatTimestamp(log.createdAt)}</TableCell>
                        <TableCell>{log.status}</TableCell>
                        <TableCell>{getLogIdentifier(log)}</TableCell>
                        <TableCell>{log.fromVersion ?? "-"}</TableCell>
                        <TableCell>{log.toVersion}</TableCell>
                        <TableCell>
                          {log.firmwareVersion?.version ?? "-"}
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(log.completedAt)}
                        </TableCell>
                        <TableCell className="max-w-[280px] truncate">
                          {log.errorMessage ?? log.technicalLog ?? "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {(logCursorHistory.length > 0 || logsPageInfo?.hasNextPage) && (
                <div className="flex justify-end gap-2 border-t p-3">
                  {logCursorHistory.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={logsLoading}
                      onClick={goToPreviousLogPage}
                    >
                      Previous install logs
                    </Button>
                  )}
                  {logsPageInfo?.hasNextPage && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={logsLoading}
                      onClick={goToNextLogPage}
                    >
                      Next install logs
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PageTemplate>
  );
};

export default FirmwarePage;
